import { dirname } from 'path';
import { LoadingManager, WebGLRenderer } from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader as BaseGLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

import { MSFTLod, SPZDollhouse } from '@spazious/dollhouse-plugins';

export type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

export interface GLTFLoaderInitOptions {
  manager?: LoadingManager;
  draco: boolean;
  ktx2: boolean;
}

export interface GLTFLoaderOptions extends GLTFLoaderInitOptions {
  autoInit?: boolean;
}

export let dracoLoader: DRACOLoader | null = null;
export let ktx2Loader: KTX2Loader | null = null;

/**
 * ### Spazious GLTF Loader
 *
 * This is a wrapper around the Three.js GLTFLoader that adds support for
 * Spazious extensions.
 *
 *
 * #### Supported Extensions
 *
 * - KHR_binary_gltf
 * - KHR_draco_mesh_compression
 * - KHR_draco_mesh_compression
 * - KHR_materials_ior
 * - KHR_materials_specular
 * - KHR_materials_transmission
 * - KHR_materials_iridescence
 * - KHR_materials_unlit
 * - KHR_materials_volume
 * - KHR_mesh_quantization
 * - KHR_lights_punctual (partial)
 * - KHR_texture_basisu
 * - KHR_texture_transform
 * - EXT_texture_webp
 * - EXT_meshopt_compression
 * - EXT_mesh_gpu_instancing
 *
 * #### Additional Extensions
 *
 * - SPZ_dollhouse
 * - KHR_environment_map
 * - MSFT_lod
 *
 * #### Pending to implement
 *
 * - KHR_materials_variants
 * - KHR_materials_emissive_strength
 */

export class GLTFLoader extends BaseGLTFLoader {
  private renderer?: WebGLRenderer;
  private options: GLTFLoaderOptions;
  private static manager = new LoadingManager();

  constructor(options?: GLTFLoaderOptions) {
    super();
    this.options = {
      autoInit: true,
      draco: true,
      ktx2: true,
      manager: GLTFLoader.manager,
      ...options,
    };

    this.register((parser) => {
      const spzExtension = new SPZDollhouse(parser);

      if (this.renderer) {
        spzExtension.setRenderer(this.renderer);
      }
      return spzExtension;
    });

    this.register(
      (parser) =>
        new MSFTLod(parser, {
          loadingMode: 'all',
        })
    );

    if (this.options.autoInit) {
      GLTFLoader.init(this.options);
    }

    if (this.options.draco) {
      if (!dracoLoader) {
        throw new Error(
          'DRACOLoader not initialized, please call GLTFLoader.init({ draco: true })'
        );
      }

      this.setDRACOLoader(dracoLoader);
    }

    if (this.options.ktx2) {
      if (!ktx2Loader) {
        throw new Error('KTX2Loader not initialized, please call GLTFLoader.init({ ktx2: true })');
      }
      this.setKTX2Loader(ktx2Loader);
    }

    // this.setMeshoptDecoder(MeshoptDecoder);
    this.setCrossOrigin('anonymous');
  }

  detectSupport(renderer: WebGLRenderer): this {
    GLTFLoader.detectSupport((this.renderer = renderer));
    return this;
  }

  /**
   * Preload the required modules modules.
   */
  static preload() {
    GLTFLoader.init();
    if (dracoLoader) {
      dracoLoader.preload();
    }
  }

  static detectSupport(renderer: WebGLRenderer) {
    if (!ktx2Loader) {
      throw new Error('KTX2Loader not initialized, please call GLTFLoader.init({ ktx2: true})');
    }
    ktx2Loader.detectSupport(renderer);
  }

  /**
   * Register the modules used for draco decoding and ktx2 texture loading.
   */
  static init(options?: GLTFLoaderInitOptions) {
    if (options?.draco) {
      const [dracoWrapper] = [
        new URL('three/examples/jsm/libs/draco/draco_wasm_wrapper.js', import.meta.url),
        new URL('three/examples/jsm/libs/draco/draco_decoder.wasm', import.meta.url),
      ];

      if (!dracoLoader) {
        dracoLoader = new DRACOLoader(options?.manager);
        dracoLoader.setDecoderConfig({ type: 'wasm' });
        dracoLoader.setDecoderPath(`${dirname(dracoWrapper.toString())}/`);
      }
    }

    if (options?.ktx2) {
      const [ktx2Wrapper] = [
        new URL('three/examples/jsm/libs/basis/basis_transcoder.js', import.meta.url),
        new URL('three/examples/jsm/libs/basis/basis_transcoder.wasm', import.meta.url),
      ];

      if (!ktx2Loader) {
        ktx2Loader = new KTX2Loader(options?.manager);
        ktx2Loader.setTranscoderPath(`${dirname(ktx2Wrapper.toString())}/`);
      }
    }
  }
}

export default GLTFLoader;
